import { useEffect, useState } from "react";
import { Sparkles, Ticket, X, Clock, Crown } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import Countdown from "./Countdown";

const STORAGE_KEY = "otra_launch_modal_dismissed_at";
const SUPPRESS_HOURS = 24;

/**
 * Modal de pré-ouverture affichée après connexion tant que :
 *   - `is_before_launch === true`
 *   - l'utilisateur n'a pas cliqué "J'ai compris" dans les dernières 24h
 *
 * Suppression via localStorage. Aucun blocage : la modale est strictement
 * informative et offre 3 actions (Achat, MesCodes, Fermer).
 */
export default function LaunchAnnouncementModal({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [config, setConfig] = useState(null);
  const [open, setOpen] = useState(false);

  // Suppress the informational modal while the user is going through
  // mandatory setup screens (first-time nickname flow) so it doesn't
  // overlay the actual required action.
  const isMandatorySetup =
    location.pathname === "/profile" && location.search.includes("need=nickname");

  useEffect(() => {
    if (!user || user === false || user === null) return;
    if (isMandatorySetup) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/launch/config");
        if (cancelled) return;
        setConfig(data);
        if (!data?.launch_message_enabled) return;
        if (!data?.is_before_launch) return;
        // Has the user dismissed it in the last 24h?
        const dismissedAt = Number(localStorage.getItem(STORAGE_KEY) || 0);
        const cutoff = Date.now() - SUPPRESS_HOURS * 60 * 60 * 1000;
        if (dismissedAt > cutoff) return;
        setOpen(true);
      } catch (e) {
        // silent — modal is non-blocking
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
    setOpen(false);
  };

  if (!open || !config || isMandatorySetup) return null;

  return (
    <div
      data-testid="launch-modal"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in"
    >
      <div className="relative w-full max-w-lg rounded-3xl border border-amber-400/40 bg-[#0a0a14] shadow-[0_0_80px_rgba(251,191,36,0.25)] overflow-hidden">
        <button
          data-testid="launch-modal-close"
          onClick={dismiss}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/60 hover:text-white transition-all z-10"
          aria-label="Fermer"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-amber-400/15 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-16 -left-16 w-44 h-44 rounded-full bg-fuchsia-500/10 blur-3xl pointer-events-none" />
        <div className="relative px-7 py-8 sm:px-9 sm:py-10">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-amber-400/40 bg-amber-400/10 text-amber-200 text-[10px] uppercase tracking-[0.3em] font-mono-stat mb-4">
            <Sparkles className="w-3 h-3" />
            Pré-ouverture
          </span>
          <h2
            data-testid="launch-modal-title"
            className="font-display font-black uppercase tracking-tighter text-3xl sm:text-4xl leading-tight"
          >
            Les premiers <span className="text-amber-300">quiz</span>
            <br />
            arrivent bientôt !
          </h2>
          <p className="mt-3 text-sm sm:text-base text-white/75 leading-relaxed">
            Le lancement officiel d&apos;OtraQuizz est prévu le{" "}
            <span className="text-amber-200 font-semibold">1er septembre 2026</span>.
            Tu peux déjà acheter tes codes de participation et les retrouver dans
            «&nbsp;Mes codes&nbsp;». Ils seront utilisables dès l&apos;ouverture des premiers
            défis.
          </p>

          <div className="mt-5 flex items-center gap-2 px-4 py-3 rounded-xl bg-white/5 border border-white/10">
            <Clock className="w-4 h-4 text-cyan-300 shrink-0" />
            <span
              data-testid="launch-modal-countdown"
              className="text-sm font-mono-stat text-cyan-100"
            >
              <Countdown
                target={config.launch_date}
                kind="opening"
                dataTestid="launch-modal-countdown-value"
              />
            </span>
          </div>

          {config.is_offer_active && (
            <div
              data-testid="launch-modal-offer"
              className="mt-5 rounded-xl border border-fuchsia-400/40 bg-gradient-to-br from-fuchsia-500/10 to-amber-400/10 p-4"
            >
              <div className="flex items-center gap-2 mb-2">
                <Crown className="w-4 h-4 text-amber-300" />
                <span className="text-[10px] uppercase tracking-[0.3em] font-mono-stat text-amber-200">
                  Offre pré-ouverture
                </span>
              </div>
              <div className="font-display font-bold text-base">
                Pack Découverte boosté
              </div>
              <p className="text-sm text-white/70 mt-1">
                Jusqu&apos;au 31 août 2026, c&apos;est{" "}
                <span className="text-amber-200 font-semibold">
                  7 codes standards pour 5 €
                </span>{" "}
                (5 achetés + 2 offerts).
              </p>
            </div>
          )}

          <div className="mt-6 flex flex-col sm:flex-row gap-2.5">
            <button
              data-testid="launch-modal-cta-buy"
              onClick={() => {
                dismiss();
                navigate("/acheter");
              }}
              className="flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-amber-400 hover:bg-amber-300 text-black font-display font-bold uppercase tracking-wider text-sm active:scale-95 transition-all glow-primary"
            >
              <Sparkles className="w-4 h-4" />
              Profiter de l&apos;offre
            </button>
            <button
              data-testid="launch-modal-cta-mycodes"
              onClick={() => {
                dismiss();
                navigate("/mes-codes");
              }}
              className="flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-white/8 hover:bg-white/12 border border-white/15 text-white font-display font-bold uppercase tracking-wider text-sm active:scale-95 transition-all"
            >
              <Ticket className="w-4 h-4" />
              Voir mes codes
            </button>
          </div>
          <button
            data-testid="launch-modal-dismiss"
            onClick={dismiss}
            className="mt-3 w-full text-center text-xs uppercase tracking-[0.3em] text-white/45 hover:text-white/70 font-mono-stat py-2 transition-colors"
          >
            J&apos;ai compris
          </button>
        </div>
      </div>
    </div>
  );
}
